/**
 * Application Routes - Module-based organization
 * 
 * Frontend routes for navigation and backend API endpoints.
 * Use helper functions for dynamic routes with parameters.
 */

// ============================================
// Frontend App Routes
// ============================================

export const APP_ROUTES = {
  HOME: "/",

  // Auth Module
  AUTH: {
    LOGIN: "/login",
    REGISTER: "/register",
    LOGOUT: "/logout",
  },

  // Onboarding Module
  ONBOARDING: {
    CONNECT_ACCOUNT: "/connect-account",
    ADD_ACCOUNT: "/add-account",
    APPEAL_REVIEW: "/appeal-review",
    DASHBOARD: '/dashboard'
  },

  // Dashboard Module
  DASHBOARD: {
    ROOT: "/dashboard",
  },

  // Profile Module
  PROFILE: {
    ROOT: "/profile",
    SETTINGS: "/settings",
  },

  // Public Pages
  PUBLIC: {
    CREATOR: (username: string) => `/${username}`,
  },
} as const;

const CREATORS_ME_PREFIX = "/creators/me";
const CREATOR_ANALYTICS_PREFIX = `${CREATORS_ME_PREFIX}/analytics` as const;

export const API_ROUTES = {
  HEALTH: "/health",

  // Auth Module
  AUTH: {
    SOCIAL: {
      REDIRECT: (provider: string) => `/auth/social/${provider}/redirect`,
      CALLBACK: (provider: string) => `/auth/social/${provider}/callback`,
      LINK_PROFILE: "/auth/social/link-profile",
    },
    ONBOARDING: {
      STATUS: "/auth/onboarding/status",
      PROFILE_READINESS: "/auth/onboarding/profile-readiness",
    },
    ENRICHMENT: {
      NICHE: "/auth/enrichment/niche",
      MANUAL_NICHE: "/auth/enrichment/manual-niche",
      PREDICT_KEYWORDS: "/auth/enrichment/predict-keywords",
      PROFILE: "/auth/enrichment/profile",
      SUMMARY: "/auth/enrichment/summary",
      CHECK_OPPORTUNITIES: "/auth/enrichment/check-opportunities",
      REQUEST_APPEAL: "/auth/enrichment/request-appeal",
    },
    LOGOUT: "/auth/logout",
  },

  // Users Module
  USERS: {
    ROOT: "/users",
    BY_ID: (id: string) => `/users/${id}`,
  },

  // Dashboard Module
  DASHBOARD: {
    ME: "/dashboard/me",
  },

  // Jobs Module
  JOBS: {
    ROOT: "/jobs",
    FILTERS: "/jobs/filters",
    COUNT: "/jobs/count",
    APPLY_CLICK: "/jobs/apply-click",
    PUBLIC: (username: string) => `/public/${username}/jobs`,
  },

  // Creators Module
  CREATORS: {
    ROOT: "/creators",
    BY_ID: (id: string) => `/creators/${id}`,
    MY_LINK_PROFILE: `${CREATORS_ME_PREFIX}/my-link-profile`,
    PROFILE: `${CREATORS_ME_PREFIX}/profile`,
    AVATAR: `${CREATORS_ME_PREFIX}/avatar`,
    SOCIAL_LINKS: `${CREATORS_ME_PREFIX}/social-links`,
    SOCIAL_LINK: (platform: string) => `${CREATORS_ME_PREFIX}/social-links/${platform}`,
    PUBLIC_PROFILE: (identifier: string) => `/creators/profile/${identifier}`,
    ANALYTICS: {
      PREFIX: CREATOR_ANALYTICS_PREFIX,
      FIRST_JOB_SHARE: `${CREATOR_ANALYTICS_PREFIX}/first-job-share`,
      FIRST_POST_STUDIO_SHARE: `${CREATOR_ANALYTICS_PREFIX}/first-post-studio-share`,
      JOB_SHARE_CLICK: `${CREATOR_ANALYTICS_PREFIX}/job-share-click`,
    },
  },

  // Analytics Module
  ANALYTICS: {
    DASHBOARD_STATS: "/analytics/dashboard-stats",
  },

  INSIGHTS: {
    OVERVIEW: "/insights/overview",
    JOBS: "/insights/jobs",
    ACTIVITY: "/insights/activity",
  },
} as const;

// ============================================
// Route Type Helpers
// ============================================

export type AppRoute = typeof APP_ROUTES;
export type ApiRoute = typeof API_ROUTES;
